"use client";
import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { HiChevronUp, HiChevronDown } from 'react-icons/hi';
import { useRouter, usePathname } from "next/navigation";
import { useLocale } from "next-intl";

export default function LanguageSwitcher() {
  const router = useRouter();
  const pathname = usePathname() || '/'; // Fallback if pathname is null
  const currentLocale = useLocale();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages = [
    { code: "en", label: "English" },
    { code: "ru", label: "Русский" },
    { code: "th", label: "ไทย" }
  ];

  const toggleMenu = () => setIsOpen((prev) => !prev);

  const handleLanguageChange = (locale: string) => {
    if (locale !== currentLocale) {
      setIsOpen(false);
      const newPathname = pathname.replace(`/${currentLocale}`, '') || '/';
      router.replace(`/${locale}${newPathname}`);
    }
  };

  // Close the dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Animation variants
  const itemVariants = {
    hidden: { opacity: 0 },
    visible: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.3,
      },
    }),
  };

  const containerVariants = {
    hidden: { height: 0, opacity: 0 },
    visible: {
      height: "auto",
      opacity: 1,
      transition: {
        duration: 0.3,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className="flex items-center px-2 py-2 bg-[#005F70] text-white rounded-md text-[12px]"
        onClick={toggleMenu}
      >
        <span>{currentLocale.toUpperCase()}</span>
        <motion.div
          className="ml-[3px] flex items-center"
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          {isOpen ? <HiChevronUp size={20} /> : <HiChevronDown size={20} />}
        </motion.div>
      </button>

      {isOpen && (
        <motion.ul
          className="absolute left-0 mt-[12px] w-[120px] bg-white border rounded-md shadow-lg overflow-hidden"
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {languages.map((lang, i) => (
            <motion.li
              key={lang.code}
              className="px-4 py-2 text-black hover:bg-gray-200 cursor-pointer text-[12px]"
              custom={i}
              initial="hidden"
              animate="visible"
              variants={itemVariants}
              onClick={() => handleLanguageChange(lang.code)}
            >
              {lang.label}
            </motion.li>
          ))}
        </motion.ul>
      )}
    </div>
  );
}
